
import React from 'react';
import { Link } from 'react-router-dom';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, CarouselDots } from '@/components/ui/carousel';
import { AnimatedSection } from '@/hooks/useScrollAnimation';
// Mamárias
import imgMastopexiaProtese from '@/assets/mastopexia-com-protese-mamaria.webp';
import imgMastopexiaSemProtese from '@/assets/mastopexia-sem-protese-elevacao-mamaria.webp';
import imgMamoplastia from '@/assets/mamoplastia-aumento-silicone.webp';
import imgTrocaProtese from '@/assets/troca-protese-mamaria-revisao.webp';
import imgEnxertiaGorduraMama from '@/assets/enxertia-gordura-mama-lipofilling.webp';
import imgMamoplastiaRedutora from '@/assets/mamoplastia-redutora-reducao-mamaria.webp';
// Contorno Corporal
import imgLipo from '@/assets/lipoaspiracao-contorno-corporal.webp';
import imgLipoEnxertia from '@/assets/lipoaspiracao-enxertia-glutea.webp';
import imgAbdominoplastia from '@/assets/abdominoplastia-curitiba.webp';
import imgMiniAbdomen from '@/assets/mini-abdominoplastia-flacidez-abdominal.webp';
import imgLifting from '@/assets/lifting-corporal-pos-emagrecimento.webp';
import imgCirurgiasCombinadas from '@/assets/cirurgias-combinadas-remodelamento-corporal.webp';
import imgMommy from '@/assets/mommy-makeover-pos-gestacao.webp';
// Faciais
import imgBlefaroplastia from '@/assets/blefaroplastia-cirurgia-palpebras.webp';
import imgOtoplastia from '@/assets/otoplastia-correcao-orelhas.webp';
import imgNanofat from '@/assets/enxertia-gordura-facial-nanofat.webp';
import imgPreenchimento from '@/assets/preenchimento-facial-curitiba.webp';
import imgToxina from '@/assets/toxina-botulinica-rugas.webp';
import imgCicatrizes from '@/assets/correcao-cicatrizes-cirurgia-plastica.webp';
import imgTumores from '@/assets/retirada-tumores-pele.webp';

import procedimentos from '@/data/procedimentos.json';

type Procedure = {
  title: string;
  description: string;
  image?: string;
  slug: string;
};

// Nome, descrição e ordem vêm de src/data/procedimentos.json — a mesma lista
// do menu, do rodapé, do formulário, do sitemap e do mapa do site. Aqui só
// entra a foto de cada card.
const IMAGES: Record<string, string> = {
  'mamoplastia-de-aumento': imgMamoplastia,
  'mamoplastia-redutora': imgMamoplastiaRedutora,
  'mastopexia-com-protese': imgMastopexiaProtese,
  'mastopexia-sem-protese': imgMastopexiaSemProtese,
  'troca-de-protese-mamaria': imgTrocaProtese,
  'enxertia-de-gordura-na-mama': imgEnxertiaGorduraMama,
  'lipoaspiracao': imgLipo,
  'lipoaspiracao-com-enxertia-glutea': imgLipoEnxertia,
  'abdominoplastia': imgAbdominoplastia,
  'mini-abdominoplastia': imgMiniAbdomen,
  'lifting-corporal-pos-perda-de-peso': imgLifting,
  'cirurgias-combinadas': imgCirurgiasCombinadas,
  'mommy-makeover': imgMommy,
  'blefaroplastia': imgBlefaroplastia,
  'otoplastia': imgOtoplastia,
  'lipoenxertia-na-face': imgNanofat,
  'preenchimento-facial': imgPreenchimento,
  'toxina-botulinica': imgToxina,
  'correcao-de-cicatrizes': imgCicatrizes,
  'retirada-de-tumores-de-pele': imgTumores,
};

const [cirurgiasMamarias, contornoCorporal, procedimentosFaciais]: Procedure[][] = procedimentos.map((g) =>
  g.items.map(({ slug, name, description }) => ({ slug, title: name, description, image: IMAGES[slug] })),
);

// ─── Shared photo area ────────────────────────────────────────────────────────
const CardPhoto: React.FC<{ procedure: Procedure; bgColor: string }> = ({ procedure, bgColor }) => (
  <div className="relative h-52 shrink-0 overflow-hidden">
    {procedure.image ? (
      <picture>
        <img
          src={procedure.image}
          alt={procedure.title}
          className="img-zoom"
          loading="lazy"
          decoding="async"
          width="400"
          height="208"
          sizes="(max-width: 640px) 85vw, (max-width: 1024px) 50vw, 25vw"
        />
      </picture>
    ) : (
      <div className="w-full h-full bg-dark-subtle flex flex-col items-center justify-center gap-2">
        <div className="w-10 h-10 rounded-full border border-gold/20 flex items-center justify-center">
          <div className="w-2 h-2 rounded-full bg-gold/40" />
        </div>
        <span className="text-light-muted/40 text-[10px] uppercase tracking-widest">Foto em breve</span>
      </div>
    )}
    <div
      className="overlay-inert"
      style={{ background: `linear-gradient(to top, ${bgColor} 0%, ${bgColor}00 55%)` }}
    />
  </div>
);

// ─── Card Styles ──────────────────────────────────────────────────────────────

// Style 1 — Mamárias: Gold top bar + index number
const MamariaCard: React.FC<{ procedure: Procedure; onOpenBooking: (p: string) => void }> = ({ procedure, onOpenBooking }) => (
    <article className="group card-tile bg-dark-surface border-white/5 hover:border-gold/30" style={{ willChange: 'transform' }}>
    <div className="h-0.5 w-full bg-gradient-to-r from-gold/60 via-gold to-gold/20" aria-hidden="true" />
    <CardPhoto procedure={procedure} bgColor="#0D2F40" />
    <div className="flex flex-col flex-1 p-5 md:p-6">
      <h4 className="card-title">
        {procedure.title}
      </h4>
      <p className="text-light-muted text-sm leading-relaxed flex-1 mb-4">
        {procedure.description}
      </p>
      <div className="row-meta">
        <Link
          to={`/procedimentos/${procedure.slug}`}
          className="link-gold-sm"
        >
          Saiba mais
        </Link>
        <button
          onClick={() => onOpenBooking(procedure.title)}
          className="btn-chip"
        >
          Agendar consulta
        </button>
      </div>
    </div>
  </article>
);

// Style 2 — Contorno Corporal: Photo card
const ContornoCard: React.FC<{ procedure: Procedure; onOpenBooking: (p: string) => void }> = ({ procedure, onOpenBooking }) => (
  <article className="group card-tile bg-dark-elevated border-white/8 hover:border-gold/25" style={{ willChange: 'transform' }}>
    <CardPhoto procedure={procedure} bgColor="#0F2E3D" />
    <div className="flex flex-col flex-1 p-5 md:p-6">
      <h4 className="card-title">
        {procedure.title}
      </h4>
      <p className="text-light-muted text-sm leading-relaxed flex-1 mb-4">
        {procedure.description}
      </p>
      <div className="row-meta">
        <Link
          to={`/procedimentos/${procedure.slug}`}
          className="link-gold-sm"
        >
          Saiba mais
        </Link>
        <button
          onClick={() => onOpenBooking(procedure.title)}
          className="btn-chip"
        >
          Agendar consulta
        </button>
      </div>
    </div>
  </article>
);

// Style 3 — Faciais: Gold left border accent
const FacialCard: React.FC<{ procedure: Procedure; onOpenBooking: (p: string) => void }> = ({ procedure, onOpenBooking }) => (
  <article className="group card-tile bg-dark-base border-white/5 hover:border-gold/20" style={{ willChange: 'transform' }}>
    <CardPhoto procedure={procedure} bgColor="#041318" />
    <div className="flex flex-col flex-1 p-6 border-l-2 border-gold/40 group-hover:border-gold transition-colors duration-300">
      <h4 className="text-base md:text-lg text-light-primary font-medium leading-tight mb-3">
        {procedure.title}
      </h4>
      <p className="text-light-muted text-sm leading-relaxed flex-1 mb-5">
        {procedure.description}
      </p>
      <div className="row-meta">
        <Link
          to={`/procedimentos/${procedure.slug}`}
          className="link-gold-sm"
        >
          Saiba mais
        </Link>
        <button
          onClick={() => onOpenBooking(procedure.title)}
          className="btn-chip"
        >
          Agendar consulta
        </button>
      </div>
    </div>
  </article>
);

// ─── Main Component ───────────────────────────────────────────────────────────

interface ProceduresSectionProps {
  onOpenBooking: (procedure: string) => void;
}

const ProceduresSection: React.FC<ProceduresSectionProps> = ({ onOpenBooking }) => {
  return (
    <section id="procedimentos" aria-labelledby="procedimentos-title" className="relative">
      <h2 id="procedimentos-title" className="sr-only">Procedimentos</h2>

      {/* ── 1. Cirurgias Mamárias — bg-dark-base ── */}
      <section aria-labelledby="cirurgias-mamarias-title" className="bg-dark-base relative">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] decorative-blur decorative-blur-primary opacity-10 pointer-events-none" aria-hidden="true" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div id="cirurgias-mamarias" className="section-spacing">
            <AnimatedSection>
              <header className="mb-12 md:mb-16">
                <p className="subtitle-wide text-gold mb-4 block">Procedimentos</p>
                <h3 id="cirurgias-mamarias-title" className="text-3xl md:text-4xl lg:text-5xl text-light-primary mb-4 leading-tight tracking-tight">
                  Cirurgias Mamárias
                </h3>
                <p className="text-base md:text-lg text-light-muted max-w-2xl leading-relaxed">
                  Procedimentos indicados com critério, planejados com precisão e conduzidos com atenção à proporção, naturalidade e segurança.
                </p>
              </header>
            </AnimatedSection>
            <AnimatedSection delay={150}>
              <div className="max-w-7xl mx-auto relative">
                <Carousel opts={{ align: "start", loop: true }} className="w-full">
                  <CarouselContent className="-ml-3 md:-ml-4 pb-4">
                    {cirurgiasMamarias.map((procedure, index) => (
                      <CarouselItem key={index} className="pl-3 md:pl-4 basis-[85%] sm:basis-1/2 lg:basis-1/3">
                        <MamariaCard procedure={procedure} onOpenBooking={onOpenBooking} />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="carousel-arrow -left-5" />
                  <CarouselNext className="carousel-arrow -right-5" />
                  <CarouselDots />
                </Carousel>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ── 2. Contorno Corporal — bg-dark-elevated ── */}
      <section aria-labelledby="contorno-corporal-title" className="bg-dark-elevated relative">
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] decorative-blur decorative-blur-accent opacity-10 pointer-events-none" aria-hidden="true" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div id="contorno-corporal" className="section-spacing">
            <AnimatedSection>
              <header className="text-center mb-12 md:mb-16">
                <p className="subtitle-wide text-gold mb-4 block">Procedimentos</p>
                <h3 id="contorno-corporal-title" className="text-3xl md:text-4xl lg:text-5xl text-light-primary mb-4 leading-tight tracking-tight">
                  Contorno Corporal
                </h3>
                <p className="text-base md:text-lg text-light-muted max-w-2xl mx-auto leading-relaxed">
                  Cada procedimento é planejado com base na anatomia, composição corporal e objetivos individuais de cada paciente.
                </p>
              </header>
            </AnimatedSection>
            <AnimatedSection delay={150}>
              <div className="max-w-7xl mx-auto relative">
                <Carousel opts={{ align: "start", loop: true }} className="w-full">
                  <CarouselContent className="-ml-3 md:-ml-4">
                    {contornoCorporal.map((procedure, index) => (
                      <CarouselItem key={index} className="pl-3 md:pl-4 basis-[85%] sm:basis-1/2 lg:basis-1/4">
                        <ContornoCard procedure={procedure} onOpenBooking={onOpenBooking} />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="carousel-arrow -left-5" />
                  <CarouselNext className="carousel-arrow -right-5" />
                  <CarouselDots />
                </Carousel>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ── 3. Faciais e Complementares — bg-dark-surface ── */}
      <section aria-labelledby="procedimentos-faciais-title" className="bg-dark-surface relative">
        <div className="absolute top-1/3 right-0 w-[400px] h-[400px] decorative-blur decorative-blur-primary opacity-8 pointer-events-none" aria-hidden="true" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div id="procedimentos-faciais" className="section-spacing">
            <AnimatedSection>
              <header className="mb-12 md:mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                  <p className="subtitle-wide text-gold mb-4 block">Procedimentos</p>
                  <h3 id="procedimentos-faciais-title" className="text-3xl md:text-4xl lg:text-5xl text-light-primary leading-tight tracking-tight">
                    Faciais e Pequenas Cirurgias
                  </h3>
                </div>
                <p className="text-base text-light-muted max-w-sm leading-relaxed md:text-right">
                  Procedimentos realizados com anestesia local, sob avaliação criteriosa e com alta logo após o procedimento.
                </p>
              </header>
            </AnimatedSection>
            <AnimatedSection delay={150}>
              <div className="max-w-7xl mx-auto relative">
                <Carousel opts={{ align: "start", loop: true }} className="w-full">
                  <CarouselContent className="-ml-3 md:-ml-4">
                    {procedimentosFaciais.map((procedure, index) => (
                      <CarouselItem key={index} className="pl-3 md:pl-4 basis-[85%] sm:basis-1/2 lg:basis-1/3">
                        <FacialCard procedure={procedure} onOpenBooking={onOpenBooking} />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="carousel-arrow -left-5" />
                  <CarouselNext className="carousel-arrow -right-5" />
                  <CarouselDots />
                </Carousel>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

    </section>
  );
};

export default ProceduresSection;
